import { useState, useEffect } from "react";
import { Moon as MoonIcon } from "lucide-react";
import { api } from "@/services/api";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MoonPhaseCard } from "@/components/moon/MoonPhaseCard";
import { SunTimesGrid } from "@/components/moon/SunTimesGrid";
import { ViewingTips } from "@/components/moon/ViewingTips";

export default function MoonPage({ zipCode: initialZipCode }) {
  const [moon, setMoon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zipCode, setZipCode] = useState(initialZipCode || "32920");
  const [inputZip, setInputZip] = useState(initialZipCode || "32920");

  const fetchMoon = async (zip) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getMoon(zip);
      setMoon(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMoon(zipCode);
  }, [zipCode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (/^\d{5}$/.test(inputZip)) {
      setZipCode(inputZip);
      localStorage.setItem("florida_zip", inputZip);
      toast.success("Location updated!");
    } else {
      toast.error("Please enter a valid 5-digit Florida zip code");
    }
  };

  return (
    <div className="min-h-screen" data-testid="moon-page">
      <div className="hero-gradient fixed inset-0 pointer-events-none" />
      
      <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
        <PageHeader 
          icon={MoonIcon}
          title="Moon Phase"
          subtitle="Current lunar cycle and sun times for your Florida location"
        />

        <form onSubmit={handleSubmit} className="glass-card p-4 mb-6 flex gap-3">
          <Input
            type="text"
            placeholder="Enter Florida zip code"
            value={inputZip}
            onChange={(e) => setInputZip(e.target.value)}
            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500"
            maxLength={5}
            data-testid="zip-input"
          />
          <Button type="submit" className="btn-primary" data-testid="update-zip-btn">
            Update
          </Button>
        </form>

        {error ? (
          <ErrorState message={error} onRetry={() => fetchMoon(zipCode)} />
        ) : loading ? (
          <LoadingState message="Loading moon data..." />
        ) : moon && (
          <div className="space-y-6">
            <MoonPhaseCard moon={moon} />
            <SunTimesGrid moon={moon} />
            <ViewingTips />
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}
